import { useCallback, useEffect, useRef, useState } from 'react';

import {
  claimTabSlot,
  clearLegacyRegistry,
  getTabId,
  HEARTBEAT_MS,
  inspectTabSlot,
  releaseTabSlot,
  type SlotStatus,
  TAB_SLOT_PREFIX,
  touchTabSlot,
} from './tabRegistry';

export type TabSlotState =
  /** First claim hasn't run yet — one frame at most. */
  | 'claiming'
  /** Holds a slot and may run. */
  | 'admitted'
  /** Refused on arrival. Nothing was ever rendered here, so nothing is lost. */
  | 'denied'
  /** Lost its slot while the user was elsewhere, and they have not come back to it yet. */
  | 'evicted'
  /** Lost its slot AND the user has since returned to it. Terminal — the caller redirects. */
  | 'expired';

export type TabSlot = {
  state: TabSlotState;
  /** True for the one tab that owns the session-refresh interval (see useSessionTimer). */
  isLeader: boolean;
  /** Re-attempt the claim — wired to the "Try again" button on the blocked screens. */
  retry: () => void;
  /**
   * This tab's identity in the registry, stable for the life of the document. Exposed so other
   * cross-tab bookkeeping can key off the SAME id rather than minting a second one — the
   * editor-lock registry needs it to tell "my other tab still has this workflow open" from
   * "that tab is gone", and a parallel id scheme would disagree with the liveness sweep.
   */
  tabId: string;
};

/**
 * Holds this tab's place in the cross-tab slot registry (see tabRegistry.ts) for as long as
 * the component is mounted, and reports whether the tab may run and whether it leads.
 *
 * <h3>The three ways a tab can be blocked</h3>
 * They are told apart deliberately, because the recovery differs.
 *
 * - `denied` — refused on arrival. Nothing has rendered, so a full-page notice is safe.
 * - `evicted` — lost its slot while the user was looking at another tab. It may be holding
 *   advisor answers that FormShell keeps in React memory and nowhere else, so the caller
 *   overlays a notice rather than unmounting the tree. Still recoverable: the heartbeat
 *   re-tests admission, and `retry` reclaims the slot outright.
 * - `expired` — the user has come BACK to a tab that had already lost its slot. Terminal: the
 *   caller redirects to the auth error page. Note this is the one transition that discards
 *   whatever the tab was holding, since routing away unmounts FormShell.
 *
 * A returning user does NOT silently reclaim the slot, which is why `visit` and `retry` differ.
 * Reclaiming on return would evict whichever tab they had just been working in, so simply
 * cycling between two tabs would keep kicking each one out from under the other.
 */
export function useTabSlot(): TabSlot {
  const tabIdRef = useRef('');
  if (!tabIdRef.current) tabIdRef.current = getTabId();

  const [state, setStateValue] = useState<TabSlotState>('claiming');
  const [isLeader, setIsLeader] = useState(false);
  /** Distinguishes 'denied' from 'evicted' — see the doc comment. */
  const admittedOnceRef = useRef(false);
  /**
   * Mirrors `state` for the event handlers. They are registered once and would otherwise close
   * over the state at mount ('claiming') forever, so a returning user would never be seen to
   * come back to an evicted tab.
   */
  const stateRef = useRef<TabSlotState>('claiming');

  const setState = useCallback((next: TabSlotState) => {
    stateRef.current = next;
    setStateValue(next);
  }, []);

  const apply = useCallback(
    (result: SlotStatus) => {
      // Terminal: the caller is already routing away, and letting a heartbeat or a neighbour's
      // write flip this back to 'admitted' would cancel that redirect mid-flight.
      if (stateRef.current === 'expired') return;
      if (result.admitted) {
        admittedOnceRef.current = true;
        setIsLeader(result.isLeader);
        setState('admitted');
        return;
      }
      setIsLeader(false);
      setState(admittedOnceRef.current ? 'evicted' : 'denied');
    },
    [setState],
  );

  /** Renew liveness (writes `lastSeen` only — never `lastActive`; see tabRegistry invariant 1). */
  const evaluate = useCallback(() => {
    apply(claimTabSlot(tabIdRef.current));
  }, [apply]);

  /**
   * The user is looking at this tab. Normally that stamps `lastActive` and keeps the slot.
   *
   * But if the tab had already lost its slot, coming back to it is terminal rather than a
   * silent reclaim: reclaiming here would evict whichever tab the user was just working in,
   * so alternating between two tabs would kick each one out from under the other.
   */
  const visit = useCallback(() => {
    if (stateRef.current === 'evicted' || stateRef.current === 'expired') {
      setIsLeader(false);
      setState('expired');
      return;
    }
    apply(touchTabSlot(tabIdRef.current));
  }, [apply, setState]);

  /**
   * Deliberate "I want THIS tab" from the blocked screen's button. Unlike `visit` this does
   * reclaim the slot, because the user asked for it explicitly rather than by glancing back.
   */
  const retry = useCallback(() => {
    apply(touchTabSlot(tabIdRef.current));
  }, [apply]);

  /** React to another tab's write (reads only — see inspectTabSlot on why). */
  const observe = useCallback(() => {
    if (stateRef.current === 'expired') return;
    const seen = inspectTabSlot(tabIdRef.current);
    // No entry means we were pruned or clobbered, not demoted. Staying put lets the next
    // heartbeat re-claim quietly, rather than flashing a blocked screen at a tab that is
    // about to get its slot straight back.
    if (!seen.present) return;
    setIsLeader(seen.isLeader);
    if (seen.admitted) {
      admittedOnceRef.current = true;
      setState('admitted');
    } else {
      setState(admittedOnceRef.current ? 'evicted' : 'denied');
    }
  }, [setState]);

  useEffect(() => {
    const tabId = tabIdRef.current;
    // Opening a tab IS visiting it, so the first write stamps `lastActive` and the newcomer
    // ranks first — which is what lets it in and pushes the least recently visited tab out.
    clearLegacyRegistry();
    visit();

    const heartbeatId = window.setInterval(evaluate, HEARTBEAT_MS);

    const onStorage = (event: StorageEvent) => {
      // `key` is null when another tab called localStorage.clear(), which wipes every slot.
      if (event.key === null || event.key.startsWith(TAB_SLOT_PREFIX)) observe();
    };
    // Only the ARRIVING direction, and that is load-bearing twice over. Ordering-wise it is
    // sufficient: entering a tab always stamps later than entering the tab before it, so the
    // tab on screen is always the most recently visited one. And stamping on departure would
    // now be actively wrong — an evicted tab the user is leaving would expire and redirect
    // behind their back, so they would return to an error page they never walked into.
    // Tab switches happen at human speed, so this needs no throttling.
    const onVisibility = () => {
      if (!document.hidden) visit();
    };
    // visibilitychange does not fire when the user moves between two browser WINDOWS: both
    // documents stay `visible`. Window focus is what distinguishes them.
    const onFocus = () => visit();
    const onPageShow = () => visit();
    const onPageHide = () => releaseTabSlot(tabId);

    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    // Both, deliberately: pagehide is the one that fires on mobile background-kill and on
    // bfcache entry, beforeunload the one desktop reliably delivers on an explicit close.
    // releaseTabSlot is idempotent, so being called twice costs nothing.
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);

    return () => {
      window.clearInterval(heartbeatId);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
      releaseTabSlot(tabId);
    };
  }, [evaluate, observe, visit]);

  return { state, isLeader, retry, tabId: tabIdRef.current };
}
